import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchContainerAppDetails, restartContainerApp } from '../../store/slices/monitoringSlice';
import { RootState, AppDispatch } from '../../store';
import StatusBadge from '../../components/monitoring/StatusBadge';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import MetricsDisplay from '../../components/monitoring/MetricsDisplay';

// Types for metrics data structure
interface MetricPoint {
    timestamp: string;
    average?: number;
    maximum?: number;
    minimum?: number;
    count?: number;
}

interface MetricInfo {
    displayName?: string;
    unit?: string;
    data: MetricPoint[];
}

interface CategoryMetrics {
    [metricName: string]: MetricInfo;
}

interface AllMetrics {
    [category: string]: CategoryMetrics;
}

// Legacy metrics data format
interface LegacyMetricData {
    timestamp: string;
    time?: string; // Add optional time property to align with ChartDataPoint
    cpu: number;
    memory: number;
}

interface LogEntry {
    timestamp: string;
    message: string;
    level: string;
}

// Container App data structure
interface ContainerApp {
    id: string;
    name: string;
    status: string;
    resourceGroup: string;
    environment: string;
    replicas: number;
    lastUpdated: string;
    healthStatus: string;
    metrics?: AllMetrics | LegacyMetricData[];
    logs?: LogEntry[];
}

// Type for formatted chart data
interface ChartDataPoint {
    time: string;
    cpu: number | null;
    memory: number | null;
    [key: string]: string | number | null;
}

// Component props
interface MetricsDisplayProps {
    metrics: AllMetrics | undefined;
}

const ContainerAppDetailsPage: React.FC = () => {
    const { resourceGroup, appName } = useParams<{ resourceGroup: string; appName: string }>();
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const { currentContainerApp, loading, error } = useSelector((state: RootState) => state.monitoring);
    const [activeTab, setActiveTab] = useState<string>('overview');

    useEffect(() => {
        if (appName && resourceGroup) {
            dispatch(fetchContainerAppDetails({ resourceGroup, appName }));

            // Set up polling for live updates
            const interval = setInterval(() => {
                dispatch(fetchContainerAppDetails({ resourceGroup, appName }));
            }, 30000);

            return () => clearInterval(interval);
        }
    }, [dispatch, appName, resourceGroup]);

    const handleRestart = (): void => {
        if (appName && currentContainerApp && resourceGroup) {
            if (window.confirm(`Are you sure you want to restart ${currentContainerApp.name}?`)) {
                dispatch(restartContainerApp({ resourceGroup, appName }));
            }
        }
    };

    const handleBack = (): void => {
        navigate('/monitoring');
    };

    // Format CPU and Memory data for overview chart
    const formatOverviewData = (): ChartDataPoint[] => {
        if (!currentContainerApp) {
            return [];
        }

        // Handle legacy format
        if (currentContainerApp.metrics && Array.isArray(currentContainerApp.metrics)) {
            // Transform LegacyMetricData to include 'time' property for ChartDataPoint
            return (currentContainerApp.metrics as LegacyMetricData[]).map(metric => ({
                ...metric,
                time: new Date(metric.timestamp).toLocaleTimeString()
            }));
        }

        // Handle new format
        const metrics = currentContainerApp.metrics as AllMetrics;
        if (!metrics?.performance) {
            return [];
        }

        const cpuData = metrics.performance.CpuPercentage?.data || [];
        const memoryData = metrics.performance.MemoryPercentage?.data || [];

        // If no data available, return empty array
        if (cpuData.length === 0 && memoryData.length === 0) {
            return [];
        }

        // Use the CPU data timestamps as reference
        return cpuData.map((point, index) => {
            const memoryPoint = memoryData[index] || {};

            return {
                time: new Date(point.timestamp).toLocaleTimeString(),
                cpu: point.average !== undefined ? Math.round(point.average * 100) / 100 : null,
                memory: memoryPoint.average !== undefined ? Math.round(memoryPoint.average * 100) / 100 : null
            };
        });
    };

    if (loading && !currentContainerApp) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                <p>Error: {error}</p>
            </div>
        );
    }

    if (!currentContainerApp) {
        return (
            <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
                <p>Container app not found</p>
            </div>
        );
    }

    const overviewData = formatOverviewData();

    return (
        <div className="container mx-auto px-4 py-6">
            <button
                onClick={handleBack}
                className="mb-4 flex items-center text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
            >
                <span className="mr-1">?</span> Back to Monitoring
            </button>

            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800 dark:text-white">{currentContainerApp.name}</h1>
                    <div className="flex items-center mt-2">
                        <StatusBadge status={currentContainerApp.status} />
                        <span className="ml-4 text-gray-600 dark:text-gray-300">
                            Last updated: {new Date(currentContainerApp.lastUpdated).toLocaleString()}
                        </span>
                    </div>
                </div>
                <div>
                    <button
                        onClick={handleRestart}
                        className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md"
                        disabled={loading}
                    >
                        Restart App
                    </button>
                </div>
            </div>

            <div className="mb-6">
                <div className="border-b border-gray-200 dark:border-gray-700">
                    <nav className="flex -mb-px">
                        <button
                            className={`py-2 px-4 font-medium ${activeTab === 'overview'
                                    ? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400'
                                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                                }`}
                            onClick={() => setActiveTab('overview')}
                        >
                            Overview
                        </button>
                        <button
                            className={`py-2 px-4 font-medium ${activeTab === 'metrics'
                                    ? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400'
                                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                                }`}
                            onClick={() => setActiveTab('metrics')}
                        >
                            Detailed Metrics
                        </button>
                        <button
                            className={`py-2 px-4 font-medium ${activeTab === 'logs'
                                    ? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400'
                                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                                }`}
                            onClick={() => setActiveTab('logs')}
                        >
                            Logs
                        </button>
                    </nav>
                </div>
            </div>

            {activeTab === 'overview' && (
                <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                            <h2 className="text-lg font-semibold mb-3 text-gray-800 dark:text-white">Details</h2>
                            <div className="space-y-2">
                                <div className="flex justify-between">
                                    <span className="text-gray-600 dark:text-gray-400">Resource Group:</span>
                                    <span className="font-medium text-gray-800 dark:text-white">{currentContainerApp.resourceGroup}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600 dark:text-gray-400">Environment:</span>
                                    <span className="font-medium text-gray-800 dark:text-white">{currentContainerApp.environment}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600 dark:text-gray-400">Replicas:</span>
                                    <span className="font-medium text-gray-800 dark:text-white">{currentContainerApp.replicas}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600 dark:text-gray-400">Health Status:</span>
                                    <span className="font-medium text-gray-800 dark:text-white">{currentContainerApp.healthStatus}</span>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                            <h2 className="text-lg font-semibold mb-3 text-gray-800 dark:text-white">Resource Usage</h2>
                            {overviewData.length > 0 ? (
                                <div className="h-64">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart
                                            data={overviewData}
                                            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="time" />
                                            <YAxis yAxisId="left" tickFormatter={(value: number) => `${value}%`} />
                                            <YAxis yAxisId="right" orientation="right" tickFormatter={(value: number) => `${value}%`} />
                                            <Tooltip formatter={(value: number) => [`${value}%`]} />
                                            <Legend />
                                            <Line yAxisId="left" type="monotone" dataKey="cpu" stroke="#8884d8" name="CPU %" />
                                            <Line yAxisId="right" type="monotone" dataKey="memory" stroke="#82ca9d" name="Memory %" />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            ) : (
                                <div className="flex justify-center items-center h-64">
                                    <p className="text-gray-500">No resource usage data available</p>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                        <h2 className="text-lg font-semibold mb-3 text-gray-800 dark:text-white">Service Health</h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                                <div className="text-gray-600 dark:text-gray-400 text-sm">Replicas</div>
                                <div className="text-2xl font-bold text-gray-800 dark:text-white mt-1">{currentContainerApp.replicas}</div>
                            </div>
                            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                                <div className="text-gray-600 dark:text-gray-400 text-sm">Status</div>
                                <div className="text-2xl font-bold text-gray-800 dark:text-white mt-1 capitalize">{currentContainerApp.status}</div>
                            </div>
                            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                                <div className="text-gray-600 dark:text-gray-400 text-sm">Health</div>
                                <div className="text-2xl font-bold text-gray-800 dark:text-white mt-1">{currentContainerApp.healthStatus}</div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'metrics' && (
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                    <h2 className="text-lg font-semibold mb-3 text-gray-800 dark:text-white">Detailed Metrics</h2>
                    {currentContainerApp.metrics && typeof currentContainerApp.metrics === 'object' && !Array.isArray(currentContainerApp.metrics) ? (
                        <MetricsDisplay metrics={currentContainerApp.metrics as AllMetrics} />
                    ) : (
                        <div className="flex justify-center items-center h-64">
                            <p className="text-gray-500">Detailed metrics are not available in this format.</p>
                        </div>
                    )}
                </div>
            )}

            {/*activeTab === 'logs' && (
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                    <h2 className="text-lg font-semibold mb-3 text-gray-800 dark:text-white">Container Logs</h2>
                    {currentContainerApp.logs && currentContainerApp.logs.length > 0 ? (
                        <div className="bg-gray-900 text-gray-100 p-4 rounded-md font-mono text-sm h-96 overflow-y-auto">
                            {currentContainerApp.logs.map((log, index) => (
                                <div key={index} className="mb-1">
                                    <span className="text-gray-400">[{new Date(log.timestamp).toLocaleString()}]</span>{' '}
                                    <span className={`mr-2 ${log.level === 'error' ? 'text-red-400' : log.level === 'warning' ? 'text-yellow-400' : 'text-blue-400'}`}>
                                        {log.level.toUpperCase()}:
                                    </span>
                                    {log.message}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="flex justify-center items-center h-64">
                            <p className="text-gray-500">No logs available. Configure Log Analytics to view logs.</p>
                        </div>
                    )}
                </div>
            )*/}
        </div>
    );
};



export default ContainerAppDetailsPage;